// src/pages/Archive/ArchivePage.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Spin, Card, Typography, Space, Tag, Select, DatePicker } from "antd";
import { UserOutlined } from "@ant-design/icons";
import BlogLayout from "../../components/BlogLayout";
import { useAuth, useTags } from "../../hooks/useBlogAPI";
import api from "../../api/blogAPI";
import "./ArchivePage.css";

const { Title } = Typography;
const { RangePicker } = DatePicker;

const ArchivePage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTag, setSelectedTag] = useState(undefined);
  const [selectedDateRange, setSelectedDateRange] = useState(null);
  const { user } = useAuth();
  const { data: tags, loading: tagsLoading } = useTags();
  const navigate = useNavigate();

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      setLoading(true);
      const result = await api.getPosts(1, 100);
      const sortedArticles = [...result.data].sort(
        (a, b) => new Date(b.publishedDate) - new Date(a.publishedDate)
      );

      setArticles(sortedArticles);
    } catch (error) {
      console.error("获取文章失败:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePost = () => {
    navigate("/posts/create");
  };

  // 过滤文章：按标签和时间范围
  const filteredArticles = articles.filter((article) => {
    // 按标签过滤
    const tagMatch =
      !selectedTag || (article.tags && article.tags.includes(selectedTag));

    // 按时间范围过滤
    let dateMatch = true;
    if (selectedDateRange) {
      const articleDate = new Date(article.publishedDate);
      const [startDate, endDate] = selectedDateRange;

      dateMatch =
        articleDate >= startDate &&
        articleDate <= new Date(endDate.setHours(23, 59, 59, 999)); // 包含结束日期的整天
    }

    return tagMatch && dateMatch;
  });

  // 处理日期范围选择
  const handleDateRangeChange = (dates, dateStrings) => {
    if (dates && dates[0] && dates[1]) {
      // 如果选择了日期范围，则设置到状态
      setSelectedDateRange(dates.map((date) => date.toDate()));
    } else {
      // 如果清空了日期范围，则设置为null
      setSelectedDateRange(null);
    }
  };

  return (
    <BlogLayout user={user} onCreatePost={handleCreatePost}>
      <div className="container archive-page">
        <Card title={<Title level={2}>归档</Title>} className="archive-card">
          <div className="archive-filter">
            <Space wrap>
              <div>
                <span className="archive-filter-label">按标签筛选</span>
                <Select
                  allowClear
                  placeholder="请选择标签"
                  loading={tagsLoading}
                  value={selectedTag}
                  onChange={(value) => setSelectedTag(value)}
                  options={tags.map((tag) => ({
                    value: tag.name,
                    label: `${tag.name} (${tag.count})`,
                  }))}
                  style={{ minWidth: 220, marginLeft: 8 }}
                />
              </div>

              <div>
                <span className="archive-filter-label">按时间筛选</span>
                <RangePicker
                  style={{ marginLeft: 8 }}
                  onChange={handleDateRangeChange}
                  placeholder={["开始日期", "结束日期"]}
                />
              </div>
            </Space>
          </div>

          <Spin spinning={loading}>
            <div className="archive-timeline">
              {filteredArticles.map((article, index) => {
                const dateText = new Date(
                  article.publishedDate
                ).toLocaleDateString();
                const isLast = index === filteredArticles.length - 1;

                return (
                  <div
                    key={article.id}
                    className={`timeline-item${isLast ? " last-item" : ""}`}
                  >
                    <div className="timeline-date">{dateText}</div>
                    <div
                      className="timeline-card"
                      onClick={() => navigate(`/posts/${article.id}`)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          navigate(`/posts/${article.id}`);
                        }
                      }}
                    >
                      <div className="timeline-title">
                        <Typography.Link>{article.title}</Typography.Link>
                      </div>
                      <div className="timeline-meta">
                        <Space size="middle">
                          <span>
                            <UserOutlined /> {article.author}
                          </span>
                          {article.tags &&
                            article.tags.map((tag) => (
                              <Tag color="blue" key={tag}>
                                {tag}
                              </Tag>
                            ))}
                        </Space>
                      </div>
                      <div className="timeline-excerpt">
                        {article.content.length > 200
                          ? article.content.substring(0, 200) + "..."
                          : article.content}
                      </div>
                    </div>
                  </div>
                );
              })}
              {!loading && filteredArticles.length === 0 && (
                <div className="archive-empty">
                  {selectedTag || selectedDateRange
                    ? "暂无符合条件的文章"
                    : "暂无文章"}
                </div>
              )}
            </div>
          </Spin>
        </Card>
      </div>
    </BlogLayout>
  );
};

export default ArchivePage;
